<template>
  <div>
    <Breadcrumbs main="Accounts" title="Receipts" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Receipts</h5>
                <span class="text-muted">Record client payments and allocate them to invoices</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadReceipts"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Add Receipt
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading receipts...
              </div>
              <div v-else-if="receipts.length === 0" class="text-center py-4 text-muted">
                No receipts found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Receipt No.</th>
                      <th>Date</th>
                      <th>Client</th>
                      <th class="text-end">Amount</th>
                      <th>Payment mode</th>
                      <th>Reference</th>
                      <th>Items</th>
                      <th>Created</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="receipt in receipts" :key="receipt.receiptId">
                      <td>
                        <strong>{{ receipt.receiptNo || `#${receipt.receiptId}` }}</strong>
                      </td>
                      <td>{{ formatDate(receipt.receiptDate) }}</td>
                      <td>{{ clientName(receipt.clientId) }}</td>
                      <td class="text-end">{{ formatMoney(receipt.amount) }}</td>
                      <td>{{ receipt.receiptMode || '—' }}</td>
                      <td>{{ receipt.receiptModeNo || '—' }}</td>
                      <td>{{ receiptItemSummary(receipt) }}</td>
                      <td>{{ formatDate(receipt.createdAt) }}</td>
                      <td class="text-end">
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(receipt)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canDelete"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(receipt)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Receipt' : 'Add Receipt' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Receipt number</label>
                      <input
                        v-model="form.receiptNo"
                        class="form-control"
                        type="text"
                        maxlength="100"
                        placeholder="e.g. RCP-2026-001"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Receipt date</label>
                      <input
                        v-model="form.receiptDate"
                        class="form-control"
                        type="date"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Client <span class="text-danger">*</span></label>
                      <select
                        v-model.number="form.clientId"
                        class="form-select"
                        :required="!isEditing"
                        :disabled="isEditing"
                        @change="onClientChange"
                      >
                        <option :value="null" disabled>Select a client</option>
                        <option
                          v-for="client in clients"
                          :key="client.clientId"
                          :value="client.clientId"
                        >
                          {{ client.fullName || `Client #${client.clientId}` }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Payment mode</label>
                      <select v-model="form.receiptMode" class="form-select">
                        <option value="">Select mode</option>
                        <option v-for="mode in receiptModeOptions" :key="mode" :value="mode">
                          {{ mode }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Reference no.</label>
                      <input
                        v-model="form.receiptModeNo"
                        class="form-control"
                        type="text"
                        maxlength="100"
                        placeholder="Cheque or transaction number"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Total amount</label>
                      <input
                        class="form-control"
                        type="text"
                        :value="formatMoney(lineItemsTotal)"
                        readonly
                      >
                    </div>
                  </div>
                </div>

                <div class="mt-3">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <label class="form-label mb-0">Line items <span class="text-danger">*</span></label>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="!form.clientId"
                      @click="addLineItem"
                    >
                      <vue-feather type="plus" class="me-1" />
                      Add item
                    </button>
                  </div>
                  <p v-if="!form.clientId" class="text-muted f-12 mb-2">
                    Select a client to allocate payments to invoices.
                  </p>
                  <div v-else-if="form.receiptItems.length === 0" class="text-muted f-12 mb-2">
                    Add at least one line item allocating payment to an invoice.
                  </div>
                  <div
                    v-for="(item, index) in form.receiptItems"
                    :key="index"
                    class="row align-items-end mb-2"
                  >
                    <div class="col-md-6">
                      <label v-if="index === 0" class="form-label">Invoice</label>
                      <select
                        v-model.number="item.invoiceId"
                        class="form-select"
                        required
                      >
                        <option :value="null" disabled>Select invoice</option>
                        <option
                          v-for="invoice in selectableInvoices(item.invoiceId)"
                          :key="invoice.invoiceId"
                          :value="invoice.invoiceId"
                        >
                          {{ invoiceLabel(invoice) }}
                        </option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label v-if="index === 0" class="form-label">Amount</label>
                      <input
                        v-model.number="item.amount"
                        class="form-control"
                        type="number"
                        min="0.01"
                        step="0.01"
                        :max="maxItemAmount(item)"
                        required
                      >
                      <small v-if="item.invoiceId" class="text-muted">
                        Max: {{ formatMoney(maxItemAmount(item)) }}
                      </small>
                    </div>
                    <div class="col-md-2">
                      <button
                        type="button"
                        class="btn btn-outline-danger w-100"
                        title="Remove item"
                        @click="removeLineItem(index)"
                      >
                        <vue-feather type="trash-2" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Receipt') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="receiptToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Receipt</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="receiptToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete receipt
                <strong>{{ receiptToDelete.receiptNo || `#${receiptToDelete.receiptId}` }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                Invoice payments from this receipt will be reversed. The record is retained for audit.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="receiptToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="receiptToDelete" class="modal-backdrop fade show" @click="receiptToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listClients } from '@/services/clientService'
import { listInvoices } from '@/services/invoiceService'
import {
  listReceipts,
  createReceipt,
  updateReceipt,
  deleteReceipt,
} from '@/services/receiptService'

const RECEIPT_MODE_OPTIONS = ['CASH', 'CHEQUE', 'BANK_TRANSFER']

function hasReceiptPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const receiptPriv = user.privileges.find((p) => p.entity === 'RECEIPT')
  if (!receiptPriv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return receiptPriv[map[action]] === true
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10)
}

const emptyLineItem = () => ({
  invoiceId: null,
  amount: null,
})

const emptyForm = () => ({
  receiptNo: '',
  receiptDate: todayIsoDate(),
  clientId: null,
  receiptMode: '',
  receiptModeNo: '',
  receiptItems: [],
})

export default {
  name: 'AccountsReceipts',

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasReceiptPrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasReceiptPrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasReceiptPrivilege(this.currentUser, 'delete')
    },

    receiptModeOptions() {
      return RECEIPT_MODE_OPTIONS
    },

    clientInvoices() {
      if (!this.form.clientId) return []
      return this.invoices.filter((inv) => inv.clientId === this.form.clientId)
    },

    lineItemsTotal() {
      return this.form.receiptItems.reduce((sum, item) => {
        const amount = Number(item.amount)
        return sum + (Number.isFinite(amount) ? amount : 0)
      }, 0)
    },
  },

  data() {
    return {
      receipts: [],
      clients: [],
      invoices: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      originalReceiptItems: [],
      form: emptyForm(),
      formError: '',
      saving: false,
      receiptToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadReceipts()
    this.loadClients()
    this.loadInvoices()
  },

  methods: {
    async loadReceipts() {
      this.loading = true
      this.error = ''

      try {
        const data = await listReceipts()
        this.receipts = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view receipts.'
        } else {
          this.error = err.message || 'Failed to load receipts'
        }
      } finally {
        this.loading = false
      }
    },

    async loadClients() {
      try {
        const data = await listClients()
        this.clients = Array.isArray(data) ? data : []
      } catch {
        this.clients = []
      }
    },

    async loadInvoices() {
      try {
        const data = await listInvoices()
        this.invoices = Array.isArray(data) ? data : []
      } catch {
        this.invoices = []
      }
    },

    clientName(clientId) {
      const client = this.clients.find((c) => c.clientId === clientId)
      return client?.fullName || `#${clientId}`
    },

    invoiceLabel(invoice) {
      const no = invoice.invoiceNo || `#${invoice.invoiceId}`
      const balance = this.formatMoney(invoice.balance)
      return `${no} — balance ${balance}`
    },

    receiptItemSummary(receipt) {
      const items = receipt.receiptItems || []
      if (items.length === 0) return '—'
      if (items.length === 1) {
        const inv = this.invoices.find((i) => i.invoiceId === items[0].invoiceId)
        const label = inv?.invoiceNo || `#${items[0].invoiceId}`
        return `${label} (${this.formatMoney(items[0].amount)})`
      }
      return `${items.length} invoices`
    },

    originalItemAmount(invoiceId) {
      const original = this.originalReceiptItems.find((item) => item.invoiceId === invoiceId)
      return original ? Number(original.amount) || 0 : 0
    },

    availableBalance(invoiceId) {
      const invoice = this.invoices.find((i) => i.invoiceId === invoiceId)
      if (!invoice) return 0
      const balance = Number(invoice.balance) || 0
      if (!this.isEditing) return balance
      return balance + this.originalItemAmount(invoiceId)
    },

    selectableInvoices(currentInvoiceId) {
      const usedIds = new Set(
        this.form.receiptItems
          .map((item) => item.invoiceId)
          .filter((id) => id != null && id !== currentInvoiceId),
      )

      return this.clientInvoices.filter((invoice) => {
        if (usedIds.has(invoice.invoiceId)) return false
        if (invoice.invoiceId === currentInvoiceId) return true
        return this.availableBalance(invoice.invoiceId) > 0
      })
    },

    maxItemAmount(item) {
      if (!item.invoiceId) return undefined
      return this.availableBalance(item.invoiceId)
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    formatMoney(value) {
      if (value == null || Number.isNaN(Number(value))) return '—'
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    onClientChange() {
      this.form.receiptItems = []
    },

    addLineItem() {
      this.form.receiptItems.push(emptyLineItem())
    },

    removeLineItem(index) {
      this.form.receiptItems.splice(index, 1)
    },

    async openCreate() {
      await this.loadInvoices()
      this.isEditing = false
      this.editingId = null
      this.originalReceiptItems = []
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    async openEdit(receipt) {
      await this.loadInvoices()
      this.isEditing = true
      this.editingId = receipt.receiptId
      this.originalReceiptItems = (receipt.receiptItems || []).map((item) => ({ ...item }))
      this.form = {
        receiptNo: receipt.receiptNo || '',
        receiptDate: receipt.receiptDate || todayIsoDate(),
        clientId: receipt.clientId,
        receiptMode: receipt.receiptMode || '',
        receiptModeNo: receipt.receiptModeNo || '',
        receiptItems: (receipt.receiptItems || []).map((item) => ({
          invoiceId: item.invoiceId,
          amount: item.amount ?? null,
        })),
      }
      if (this.form.receiptItems.length === 0) {
        this.addLineItem()
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    validateLineItems() {
      if (!this.form.clientId) {
        return 'Please select a client.'
      }

      if (this.form.receiptItems.length === 0) {
        return 'Add at least one line item.'
      }

      for (const item of this.form.receiptItems) {
        if (!item.invoiceId) {
          return 'Each line item must reference an invoice.'
        }
        const amount = Number(item.amount)
        if (!Number.isFinite(amount) || amount <= 0) {
          return 'Each line item must have a positive amount.'
        }
        const max = this.availableBalance(item.invoiceId)
        if (amount > max) {
          return `Payment amount exceeds available balance for invoice ${item.invoiceId}.`
        }
      }

      const invoiceIds = this.form.receiptItems.map((item) => item.invoiceId)
      if (new Set(invoiceIds).size !== invoiceIds.length) {
        return 'Each invoice can only appear once per receipt.'
      }

      return ''
    },

    buildPayload() {
      const payload = {}

      const receiptNo = this.form.receiptNo?.trim()
      if (receiptNo) payload.receiptNo = receiptNo

      if (this.form.receiptDate) payload.receiptDate = this.form.receiptDate

      if (!this.isEditing && this.form.clientId) {
        payload.clientId = this.form.clientId
      }

      const receiptMode = this.form.receiptMode?.trim()
      if (receiptMode) payload.receiptMode = receiptMode

      const receiptModeNo = this.form.receiptModeNo?.trim()
      if (receiptModeNo) payload.receiptModeNo = receiptModeNo

      payload.receiptItems = this.form.receiptItems.map((item) => ({
        invoiceId: item.invoiceId,
        amount: Number(item.amount),
      }))

      return payload
    },

    async submitForm() {
      this.formError = ''

      const validationError = this.validateLineItems()
      if (validationError) {
        this.formError = validationError
        return
      }

      this.saving = true

      try {
        if (this.isEditing) {
          await updateReceipt({
            receiptId: this.editingId,
            ...this.buildPayload(),
          })
        } else {
          await createReceipt(this.buildPayload())
        }
        this.showFormModal = false
        await Promise.all([this.loadReceipts(), this.loadInvoices()])
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(receipt) {
      this.receiptToDelete = receipt
      this.actionError = ''
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteReceipt(this.receiptToDelete.receiptId)
        this.receiptToDelete = null
        await Promise.all([this.loadReceipts(), this.loadInvoices()])
      } catch (err) {
        this.actionError = err.message || 'Failed to delete receipt'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
